/**
 * The tail `layoutShapeTextContent` would not place, tested for ink rather than for length.
 *
 * Whitespace has none, so a tail of spaces/tabs is not text the user can see going missing — it
 * is `MAX_HANGING_SPACES` (`shape-text.ts`) reaching the end of the flow. That constant lets a
 * run of >2 spaces wrap like any other token, and at the last row there is nowhere left to wrap
 * to, so the fitter stops on the first space that did not fit and `consumedLength` reports the
 * split. A plain `consumedLength < textValue.length` then read that as overset and raised the
 * badge on a frame whose remaining rows hold nothing but invisible spaces — which is what QA
 * saw as "overflow indicator on a half-empty box".
 *
 * Newlines are deliberately NOT exempt: an empty paragraph occupies a whole row, so a `\n` that
 * did not fit is real overset with a real visual consequence.
 *
 * The exempt set started as a mirror of `isBreakSpace` in `shape-text.ts` (`' '` and `'\t'`) —
 * the characters that can hang and then break, hence the ones that can land in this tail without
 * ink. It has since grown by the two that predicted comment named:
 *
 * - **NBSP (U+00A0)** paints nothing, and an external paste delivers it in quantity: Word, Docs
 *   and ordinary web HTML emit `&nbsp;` for runs of spaces, `parse-external-clipboard-html.ts`
 *   preserves text nodes verbatim, and `filterOutOfRangeChars` (`lib/text/paste-sanitize.ts`)
 *   only rewrites code points above U+5FFF. So pasting a paragraph could leave a tail of
 *   invisible NBSPs and light the badge on a frame with visible room — WMPCM-10022.
 * - **Ideographic space (U+3000)**, same argument, same class.
 * - **U+2000-U+200A** (the en/em/thin/hair quad family), **U+2007** figure space, **U+202F**
 *   narrow no-break space, **U+205F** medium mathematical space, **U+200B** zero-width space and
 *   **U+FEFF** zero-width no-break space - every one paints nothing, and they arrive from exactly
 *   the sources that motivated NBSP above (Word and Docs emit the quad family in justified runs;
 *   U+200B and U+FEFF ride along in web HTML as line-break hints). U+2000-U+200B is written as
 *   one contiguous range because it is one, and it covers U+2007 along the way.
 *
 * Deliberately NOT paired with a change to `isBreakSpace`: refusing to break is the entire
 * purpose of a non-breaking space, so it stays one unbreakable token to the layout engine. The
 * two predicates answer different questions — "may the line wrap here" and "would the reader see
 * this go missing" — and only the second one is about ink.
 *
 * Newlines remain non-exempt (see above).
 *
 * `layout.consumedLength < textValue.length` needs no separate AND — an empty slice never
 * matches. Same whitespace-is-not-content convention as `paragraphsHoldText`
 * (`lib/text/paragraph-plain-text.ts`), and deliberately NOT the opposite one in
 * `paragraphsHaveText` (`lib/text-linking/merge-overflow-paragraphs.ts`), which counts a
 * whitespace-only run as real text because it gates whether content may be DISCARDED. This is a
 * visibility question, not a discard one.
 */
export function hasVisibleOverset(textValue: string, consumedLength: number): boolean {
  // Escapes rather than the literal characters: NBSP and U+3000 are invisible in an editor, and
  // a character class nobody can read is one nobody can review.
  return /[^ \t\u00A0\u2000-\u200B\u202F\u205F\u3000\uFEFF]/.test(textValue.slice(consumedLength))
}
